import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import api from '../lib/api';

const STATUS_COLORS = {
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-amber-100 text-amber-700',
  reconciled: 'bg-green-100 text-green-700',
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const h = dt.getHours() % 12 || 12;
  const ampm = dt.getHours() >= 12 ? 'PM' : 'AM';
  return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getFullYear()).slice(-2)} ${h}:${String(dt.getMinutes()).padStart(2, '0')} ${ampm}`;
};

const StockAudit = ({ user }) => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAudit, setActiveAudit] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [physicalCount, setPhysicalCount] = useState('');
  const [scanNotes, setScanNotes] = useState('');
  const [scanning, setScanning] = useState(false);
  const [viewAudit, setViewAudit] = useState(null);
  const scanRef = useRef(null);
  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'inventory_staff';

  useEffect(() => { fetchAudits(); }, []);

  const fetchAudits = async () => {
    try { const res = await api.get('/stock-audits'); setAudits(res.data); }
    catch { toast.error('Failed to load audits'); }
    finally { setLoading(false); }
  };

  const startNewAudit = async () => {
    try {
      const res = await api.post('/stock-audits', { notes: '' });
      toast.success(`Audit ${res.data.audit_number} started!`);
      setActiveAudit(res.data);
      fetchAudits();
      setTimeout(() => scanRef.current?.focus(), 300);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start audit'); }
  };

  const openAudit = async (audit) => {
    try {
      const res = await api.get(`/stock-audits/${audit.id}`);
      if (res.data.status === 'in_progress') {
        setActiveAudit(res.data);
        setTimeout(() => scanRef.current?.focus(), 300);
      } else {
        setViewAudit(res.data);
      }
    } catch { toast.error('Failed to load audit'); }
  };

  const handleScan = async () => {
    if (!scanInput.trim() || !physicalCount) return;
    setScanning(true);
    try {
      const res = await api.post(`/stock-audits/${activeAudit.id}/scan`, {
        barcode: scanInput.trim(),
        physical_count: parseInt(physicalCount),
        notes: scanNotes,
      });
      const item = res.data.item;
      toast.success(`${res.data.status === 'updated' ? 'Updated' : 'Added'}: ${item.design_code} ${item.color} ${item.size} — Disc: ${item.discrepancy >= 0 ? '+' : ''}${item.discrepancy}`);
      // Refresh active audit
      const updated = await api.get(`/stock-audits/${activeAudit.id}`);
      setActiveAudit(updated.data);
      setScanInput(''); setPhysicalCount(''); setScanNotes('');
      scanRef.current?.focus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally { setScanning(false); }
  };

  const completeAudit = async () => {
    if (!window.confirm('Complete this audit? You can still reconcile after.')) return;
    try {
      await api.put(`/stock-audits/${activeAudit.id}/complete`);
      toast.success('Audit completed!');
      setActiveAudit(null);
      fetchAudits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to complete'); }
  };

  const reconcileAudit = async (auditId) => {
    if (!window.confirm('This will adjust system stock to match physical counts for ALL discrepancies. Continue?')) return;
    try {
      const res = await api.put(`/stock-audits/${auditId}/reconcile`);
      toast.success(res.data.message);
      setViewAudit(null);
      fetchAudits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reconciliation failed'); }
  };

  const deleteAudit = async (id) => {
    if (!window.confirm('Delete this audit?')) return;
    try { await api.delete(`/stock-audits/${id}`); toast.success('Audit deleted'); fetchAudits(); }
    catch { toast.error('Failed to delete'); }
  };

  const auditItems = useMemo(() => activeAudit?.items || [], [activeAudit]);
  const discrepancies = useMemo(() => auditItems.filter(i => i.discrepancy !== 0), [auditItems]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  // Active audit scanning view
  if (activeAudit) {
    return (
      <div className="p-6 space-y-6 animate-fadeIn" data-testid="stock-audit-active">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
              Stock Audit — {activeAudit.audit_number}
            </h1>
            <p className="text-gray-600 mt-1">Scan barcodes or enter SKU codes to verify physical stock</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setActiveAudit(null)} data-testid="back-to-audits">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              Back
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={completeAudit} data-testid="complete-audit-btn">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
              Complete Audit
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-teal-100 bg-teal-50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-teal-700">{auditItems.length}</p>
            <p className="text-xs text-teal-600">Items Scanned</p>
          </CardContent></Card>
          <Card className="border-red-100 bg-red-50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{discrepancies.filter(d => d.discrepancy < 0).length}</p>
            <p className="text-xs text-red-600">Shortages</p>
          </CardContent></Card>
          <Card className="border-purple-100 bg-purple-50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{discrepancies.filter(d => d.discrepancy > 0).length}</p>
            <p className="text-xs text-purple-600">Excess</p>
          </CardContent></Card>
        </div>

        {/* Scan Input */}
        <Card data-testid="scan-section">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
              <Label className="font-semibold text-teal-800">Scan Barcode / Enter SKU Code</Label>
            </div>
            <div className="flex gap-3 items-end flex-wrap">
              <div className="flex-1 min-w-[200px] space-y-1">
                <Label className="text-xs text-gray-500">Barcode / SKU / Design Code</Label>
                <Input ref={scanRef} value={scanInput} onChange={e => setScanInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && scanInput && physicalCount) handleScan(); }}
                  placeholder="Scan barcode or type SKU code..." autoFocus
                  className="text-lg h-12 font-mono border-teal-300 focus:border-teal-500"
                  data-testid="scan-barcode-input" />
              </div>
              <div className="w-28 space-y-1">
                <Label className="text-xs text-gray-500">Physical Count</Label>
                <Input type="number" value={physicalCount} onChange={e => setPhysicalCount(e.target.value)}
                  onWheel={e => e.target.blur()}
                  onKeyDown={e => { if (e.key === 'Enter' && scanInput && physicalCount) handleScan(); }}
                  placeholder="Qty" min="0" className="text-lg h-12 text-center font-bold"
                  data-testid="scan-physical-count" />
              </div>
              <div className="w-40 space-y-1">
                <Label className="text-xs text-gray-500">Notes (optional)</Label>
                <Input value={scanNotes} onChange={e => setScanNotes(e.target.value)} placeholder="Remark..."
                  className="h-12" data-testid="scan-notes" />
              </div>
              <Button onClick={handleScan} disabled={!scanInput.trim() || !physicalCount || scanning}
                className="bg-teal-600 hover:bg-teal-700 h-12 px-6" data-testid="scan-submit-btn">
                {scanning ? 'Scanning...' : 'Record'}
              </Button>
            </div>
            <p className="text-xs text-gray-400">Tip: Use a barcode scanner — it auto-fills the code and you just enter the count.</p>
          </CardContent>
        </Card>

        {/* Scanned Items Table */}
        <Card>
          <CardHeader>
            <CardTitle>Scanned Items ({auditItems.length})</CardTitle>
            <CardDescription>{discrepancies.length} discrepancies found</CardDescription>
          </CardHeader>
          <CardContent>
            {auditItems.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                <p className="text-lg font-medium">No items scanned yet</p>
                <p className="text-sm">Start scanning barcodes or entering SKU codes above</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-items-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left font-semibold text-gray-600">Design</th>
                      <th className="p-2 text-left font-semibold text-gray-600">Color</th>
                      <th className="p-2 text-left font-semibold text-gray-600">Size</th>
                      <th className="p-2 text-right font-semibold text-gray-600">System Stock</th>
                      <th className="p-2 text-right font-semibold text-gray-600">Physical Count</th>
                      <th className="p-2 text-right font-semibold text-gray-600">Discrepancy</th>
                      <th className="p-2 text-left font-semibold text-gray-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...auditItems].reverse().map((item, idx) => (
                      <tr key={idx} className={`border-t ${item.discrepancy < 0 ? 'bg-red-50' : item.discrepancy > 0 ? 'bg-purple-50' : 'bg-green-50'}`}
                        data-testid={`audit-item-${idx}`}>
                        <td className="p-2 font-semibold">{item.design_code}</td>
                        <td className="p-2">{item.color}</td>
                        <td className="p-2">{item.size}</td>
                        <td className="p-2 text-right">{item.system_stock}</td>
                        <td className="p-2 text-right font-bold">{item.physical_count}</td>
                        <td className="p-2 text-right">
                          {item.discrepancy === 0 ? (
                            <Badge className="bg-green-100 text-green-700">Match</Badge>
                          ) : item.discrepancy < 0 ? (
                            <Badge className="bg-red-100 text-red-700">{item.discrepancy} (Short)</Badge>
                          ) : (
                            <Badge className="bg-purple-100 text-purple-700">+{item.discrepancy} (Excess)</Badge>
                          )}
                        </td>
                        <td className="p-2 text-xs text-gray-500">{item.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Audit list view
  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="stock-audit-list">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Stock Audit</h1>
          <p className="text-gray-600 mt-1">Physical stock verification using barcode scanning</p>
        </div>
        {canManage && (
          <Button className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 shadow-lg"
            onClick={startNewAudit} data-testid="start-audit-btn">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            Start New Audit
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audit Sessions ({audits.length})</CardTitle>
          <CardDescription>History of all stock audit sessions</CardDescription>
        </CardHeader>
        <CardContent>
          {audits.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Audits Yet</h3>
              <p className="text-gray-600">Start your first stock audit to verify physical inventory.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audits-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left font-semibold text-gray-600">Audit #</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Status</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Started</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Completed</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Items Scanned</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Discrepancies</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {audits.map(audit => (
                    <tr key={audit.id} className="border-t hover:bg-gray-50" data-testid={`audit-row-${audit.id}`}>
                      <td className="p-3 font-mono font-bold text-teal-700">{audit.audit_number}</td>
                      <td className="p-3"><Badge className={STATUS_COLORS[audit.status]}>{audit.status?.replace('_', ' ')}</Badge></td>
                      <td className="p-3 text-xs text-gray-500">{fmtDate(audit.started_at)}</td>
                      <td className="p-3 text-xs text-gray-500">{audit.completed_at ? fmtDate(audit.completed_at) : '-'}</td>
                      <td className="p-3 text-center font-semibold">{audit.total_items_scanned}</td>
                      <td className="p-3 text-center">
                        {audit.total_discrepancies > 0 ? (
                          <Badge className="bg-red-100 text-red-700">{audit.total_discrepancies}</Badge>
                        ) : <span className="text-green-600 font-medium">0</span>}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => openAudit(audit)} data-testid={`open-audit-${audit.id}`}>
                            {audit.status === 'in_progress' ? 'Continue' : 'View'}
                          </Button>
                          {canManage && (
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-8 px-2"
                              onClick={() => deleteAudit(audit.id)} data-testid={`delete-audit-${audit.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View completed audit dialog */}
      {viewAudit && (
        <Dialog open={!!viewAudit} onOpenChange={() => setViewAudit(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-50">
            <DialogHeader>
              <DialogTitle>{viewAudit.audit_number} — {viewAudit.status?.replace('_', ' ')}</DialogTitle>
              <DialogDescription>Completed: {fmtDate(viewAudit.completed_at)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-teal-50 p-3 rounded-lg text-center">
                  <p className="text-xl font-bold text-teal-700">{viewAudit.items?.length || 0}</p>
                  <p className="text-xs text-teal-600">Items Scanned</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <p className="text-xl font-bold text-red-700">{(viewAudit.items || []).filter(i => i.discrepancy < 0).length}</p>
                  <p className="text-xs text-red-600">Shortages</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <p className="text-xl font-bold text-purple-700">{(viewAudit.items || []).filter(i => i.discrepancy > 0).length}</p>
                  <p className="text-xs text-purple-600">Excess</p>
                </div>
              </div>
              {(viewAudit.items || []).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr>
                      <th className="p-2 text-left">Design</th><th className="p-2 text-left">Color</th><th className="p-2 text-left">Size</th>
                      <th className="p-2 text-right">System</th><th className="p-2 text-right">Physical</th><th className="p-2 text-right">Discrepancy</th>
                    </tr></thead>
                    <tbody>
                      {(viewAudit.items || []).map((item, idx) => (
                        <tr key={idx} className={`border-t ${item.discrepancy < 0 ? 'bg-red-50' : item.discrepancy > 0 ? 'bg-purple-50' : ''}`}>
                          <td className="p-2 font-semibold">{item.design_code}</td>
                          <td className="p-2">{item.color}</td><td className="p-2">{item.size}</td>
                          <td className="p-2 text-right">{item.system_stock}</td>
                          <td className="p-2 text-right font-bold">{item.physical_count}</td>
                          <td className="p-2 text-right">
                            {item.discrepancy === 0 ? <span className="text-green-600">Match</span> :
                              item.discrepancy < 0 ? <span className="text-red-600 font-bold">{item.discrepancy}</span> :
                                <span className="text-purple-600 font-bold">+{item.discrepancy}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {viewAudit.status === 'completed' && canManage && (
                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => reconcileAudit(viewAudit.id)}
                  data-testid="reconcile-audit-btn">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Reconcile — Adjust System Stock to Physical Counts
                </Button>
              )}
              {viewAudit.status === 'reconciled' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <svg className="w-8 h-8 mx-auto text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                  <p className="font-semibold text-green-700">This audit has been reconciled</p>
                  <p className="text-xs text-green-600">System stock has been adjusted to match physical counts</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default StockAudit;
